import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { optimizeStorageUrl } from "@/lib/optimizeStorageUrl";
import ScrollReveal from "@/components/ScrollReveal";

interface SeasonalBlock {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  recipe_slugs?: string[];
  background?: string;
}

const HomepageSeasonalBlock = () => {
  const { data: block } = useQuery({
    queryKey: ["site-setting-homepage-seasonal-block"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "homepage_seasonal_block")
        .maybeSingle();
      return (data?.value ?? null) as SeasonalBlock | null;
    },
  });

  const slugs = (block?.recipe_slugs ?? []).map((s) => (s || "").trim()).filter(Boolean);

  const { data: recipes = [] } = useQuery({
    queryKey: ["seasonal-block-recipes", slugs.join(",")],
    enabled: slugs.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("recipes")
        .select("id, slug, title, featured_image_url")
        .eq("is_published", true)
        .in("slug", slugs);
      const map = new Map((data ?? []).map((r) => [r.slug, r] as const));
      return slugs.map((s) => map.get(s)).filter(Boolean) as any[];
    },
  });

  if (!block || !block.title || recipes.length === 0) return null;

  const bg = block.background || "bg-warm-tan";

  return (
    <section className={`${bg} py-8 md:py-10 border-t border-border`}>
      <div className="container">
        <ScrollReveal className="text-center mb-6">
          {block.eyebrow && (
            <p className="font-handwritten text-primary text-2xl mb-1">{block.eyebrow}</p>
          )}
          <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">
            {block.title}
          </h2>
          {block.subtitle && (
            <p className="text-muted-foreground font-body text-sm max-w-lg mx-auto">{block.subtitle}</p>
          )}
        </ScrollReveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {recipes.slice(0, 6).map((r, i) => (
            <ScrollReveal key={r.id} delay={i * 0.05}>
              <Link to={`/recipes/${r.slug}`} className="block group">
                <div className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={optimizeStorageUrl(r.featured_image_url, 400, 400)}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <span className="cat-label">{r.title}</span>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomepageSeasonalBlock;